"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { generateBespoke, adjustBespoke, deployBespoke, deleteBespoke } from "./actions";
import type { BespokeDemo } from "./page";

const FONT = { fontFamily: "var(--font-geist-sans), system-ui, sans-serif" };
const MONO = { fontFamily: "var(--font-geist-mono), monospace" };

export function BespokeStudio({ demos }: { demos: BespokeDemo[] }) {
  const router = useRouter();
  const [url, setUrl] = useState("");
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function generate(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setErr("");
    const res = await generateBespoke(url, instr);
    setBusy(false);
    if (res.ok) {
      setUrl("");
      setInstr("");
      router.refresh();
    } else {
      setErr(res.error || "Fehler");
    }
  }

  return (
    <div style={FONT} className="bg-[#080808]">
      <section className="mx-auto max-w-5xl px-6 pb-16 pt-10">
        <div className="mb-2 flex items-center gap-3">
          <span
            style={MONO}
            className="rounded-full border border-[#ee4f27]/40 bg-[#ee4f27]/10 px-3 py-1 text-[0.6rem] uppercase tracking-[0.3em] text-[#ff8a5c]"
          >
            Opus baut von Grund auf
          </span>
        </div>
      <h2 className="text-2xl font-semibold text-white">
        <span className="bg-gradient-to-r from-[#ff6a3d] to-[#9c40ff] bg-clip-text text-transparent">
          Eine eigene Website pro Firma
        </span>
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-white/50">
        URL rein. Claude versteht die Firma und baut eine echte, individuelle Seite (kein Template).
        Dauert etwa eine Minute. Danach kannst du per Anweisung anpassen, sogar Verhalten wie
        &bdquo;Bild soll nicht mitscrollen&ldquo;.
      </p>

      <form onSubmit={generate} className="mt-6 flex flex-col gap-3">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="firma-website.de"
          disabled={busy}
          className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white outline-none placeholder:text-white/30 focus:border-[#9c40ff]/60"
        />
        <input
          value={instr}
          onChange={(e) => setInstr(e.target.value)}
          placeholder="Optional: Wünsche fürs Konzept (z.B. dunkel, edel, Fokus auf die Produkte)"
          disabled={busy}
          className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-[#9c40ff]/60"
        />
        <button
          type="submit"
          disabled={busy}
          className="self-start rounded-lg bg-gradient-to-r from-[#ee4f27] to-[#9c40ff] px-6 py-3 font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Baut… (~1 Min)" : "Website bauen"}
        </button>
        {err && <p className="text-sm text-red-300">{err}</p>}
      </form>

      <div className="mt-8 flex flex-col gap-3">
        {demos.length === 0 && (
          <p className="text-sm text-white/30">Noch keine Bespoke-Seiten. Gib oben eine URL ein.</p>
        )}
        {demos.map((d) => (
          <BespokeRow key={d.slug} demo={d} />
        ))}
      </div>
    </section>
    </div>
  );
}

function BespokeRow({ demo }: { demo: BespokeDemo }) {
  const router = useRouter();
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState<"" | "adjust" | "deploy">("");
  const [err, setErr] = useState("");
  const [tab, setTab] = useState<"none" | "dm" | "email">("none");
  const [copied, setCopied] = useState(false);

  const link = demo.live || "[Link erscheint nach Live schalten]";
  const message = (tab === "dm" ? demo.dm : demo.email).replaceAll("{LINK}", link);

  function copy() {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function adjust() {
    if (!instr.trim()) return;
    setBusy("adjust");
    setErr("");
    const res = await adjustBespoke(demo.slug, instr);
    setBusy("");
    if (res.ok) {
      setInstr("");
      router.refresh();
    } else setErr(res.error || "Fehler");
  }

  async function deploy() {
    setBusy("deploy");
    setErr("");
    const res = await deployBespoke(demo.slug);
    setBusy("");
    if (res.ok) router.refresh();
    else setErr(res.error || "Deploy fehlgeschlagen");
  }

  async function remove() {
    if (!confirm(`Bespoke-Seite "${demo.name}" löschen?`)) return;
    await deleteBespoke(demo.slug);
    router.refresh();
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="font-medium text-white">{demo.name}</span>
          {demo.live && (
            <span style={MONO} className="rounded-full bg-green-500/15 px-2 py-0.5 text-[0.6rem] uppercase tracking-widest text-green-300">
              live
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <a href={demo.preview} target="_blank" rel="noreferrer" className="text-[#9c8cff] underline-offset-2 hover:underline">
            Vorschau
          </a>
          {demo.live && (
            <a href={demo.live} target="_blank" rel="noreferrer" className="text-green-300 underline-offset-2 hover:underline">
              Live-Link
            </a>
          )}
          <button onClick={deploy} disabled={!!busy} className="rounded-md border border-white/15 px-3 py-1.5 text-white/80 hover:bg-white/5 disabled:opacity-50">
            {busy === "deploy" ? "Deployt…" : demo.live ? "Neu deployen" : "Live schalten"}
          </button>
          <button onClick={remove} disabled={!!busy} className="rounded-md border border-red-400/20 px-3 py-1.5 text-red-300/80 hover:bg-red-500/10 disabled:opacity-50">
            Löschen
          </button>
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          value={instr}
          onChange={(e) => setInstr(e.target.value)}
          placeholder='Anpassung, z.B. "dunkler", "Hero-Bild nicht mitscrollen", "mehr Fokus auf Team"'
          disabled={!!busy}
          className="flex-1 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none placeholder:text-white/30 focus:border-[#9c40ff]/60"
        />
        <button
          onClick={adjust}
          disabled={!!busy || !instr.trim()}
          className="rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/15 disabled:opacity-50"
        >
          {busy === "adjust" ? "Passt an… (~1 Min)" : "Anpassen"}
        </button>
      </div>
      {err && <p className="mt-2 text-sm text-red-300">{err}</p>}

      {(demo.dm || demo.email) && (
        <div className="mt-3 border-t border-white/5 pt-3">
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={() => setTab(tab === "dm" ? "none" : "dm")}
              className={`rounded-md px-3 py-1.5 ${tab === "dm" ? "bg-[#9c40ff]/20 text-[#c4a6ff]" : "text-white/50 hover:text-white/80"}`}
            >
              DM (Instagram)
            </button>
            <button
              onClick={() => setTab(tab === "email" ? "none" : "email")}
              className={`rounded-md px-3 py-1.5 ${tab === "email" ? "bg-[#9c40ff]/20 text-[#c4a6ff]" : "text-white/50 hover:text-white/80"}`}
            >
              E-Mail
            </button>
            {tab !== "none" && (
              <button onClick={copy} className="ml-auto rounded-md px-3 py-1.5 text-white/50 hover:text-white/80">
                {copied ? "kopiert ✓" : "kopieren"}
              </button>
            )}
          </div>
          {tab !== "none" && (
            <>
              <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-black/40 p-3 text-sm text-white/80" style={FONT}>
                {message}
              </pre>
              {!demo.live && (
                <p className="mt-1 text-[0.7rem] text-white/30">
                  Tipp: erst „Live schalten", dann steht der echte Link in der Nachricht.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
